import React, { type ReactNode, useState } from 'react'
import { Box, Chip, Divider, ListSubheader, type ButtonProps, Menu, MenuItem, type MenuProps, useTheme } from '@mui/material'
import { grey } from '../../colors'
import GenericButton, { type GenericButtonProps } from '../GenericButton'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { ButtonSize, ButtonType } from '../GenericButton/styles'
import CustomTooltip from '../CustomTooltip'
import MoreVertIcon from '@mui/icons-material/MoreVert'

export interface DropdownItemConfig {
  text: string
  action?: () => void
  disabled?: boolean
  tooltipTitle?: string | ReactNode
  visible?: boolean
  isHeader?: boolean
  isSubLabel?: boolean
  isSubItem?: boolean
  isDivider?: boolean
  chip?: { label: string, color: 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' }
}

interface MenuItemContentProps {
  onClick: () => void
  text: string
  menuStyles?: React.CSSProperties
  disabled?: boolean
  tooltipTitle?: string | ReactNode
  chip?: DropdownItemConfig['chip']
}

const MenuItemContent: React.FC<MenuItemContentProps> = ({ onClick, text, menuStyles, disabled, tooltipTitle, chip }) => {
  const theme = useTheme()
  const defaultMenuStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: grey[950],
    marginBottom: theme.spacing(1)
  }
  return (
    <CustomTooltip title={tooltipTitle}>
      <MenuItem
        disabled={disabled}
        sx={{ ...defaultMenuStyle, ...menuStyles, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        onClick={onClick}
      >
        <span>{text}</span>
        {chip != null && (
          <Chip
            label={chip.label}
            color={chip.color}
            size="small"
            sx={{ ml: 2, height: '20px', fontSize: '11px', fontWeight: 600, pointerEvents: 'none' }}
          />
        )}
      </MenuItem>
    </CustomTooltip>
  )
}

interface DropdownProps {
  menuItems: DropdownItemConfig[] | ReactNode
  label?: string | React.ReactNode
  buttonType?: ButtonType
  buttonSize?: ButtonSize
  extraButtonProps?: object | ButtonProps | GenericButtonProps
  menuProps?: MenuProps
  menuStyles?: React.CSSProperties
  menuPaperStyles?: React.CSSProperties
  disabled?: boolean
  dropdownType?: 'default' | 'action'
}

const Dropdown: React.FC<DropdownProps> = ({
  menuItems,
  label,
  buttonType = ButtonType.Secondary,
  buttonSize = ButtonSize.Medium,
  extraButtonProps,
  menuProps,
  menuStyles,
  menuPaperStyles,
  disabled = false,
  dropdownType = 'default'
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const theme = useTheme()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleMenuItemClick = (action: (() => void) | undefined): void => {
    if (action != null) action()
    handleClose()
  }

  const buttonIcon = open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
  return (
    <>
      <GenericButton
        onClick={handleClick}
        label={label}
        endIcon={dropdownType === 'default' ? buttonIcon : <MoreVertIcon />}
        fontWeight='bold'
        icon={dropdownType === 'action' && <MoreVertIcon />}
        buttonType={buttonType}
        buttonSize={buttonSize}
        disabled={disabled}
        {...extraButtonProps} />
      <Menu
        elevation={4}
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        sx={{
          '& .MuiMenu-paper': {
            borderRadius: theme.spacing(2),
            minWidth: theme.spacing(60),
            marginTop: theme.spacing(2),
            ...menuPaperStyles
          }
        }}
        {...menuProps}
      >
        {Array.isArray(menuItems)
          ? menuItems
            .map((item, originalIndex) => ({ item, originalIndex }))
            .filter(({ item }) => item.visible !== false)
            .map(({ item, originalIndex }) => {
              if (item.isDivider === true) {
                return <Divider key={originalIndex} sx={{ my: 1 }} />
              }
              if (item.isHeader === true) {
                return (
                  <ListSubheader
                    key={originalIndex}
                    disableSticky
                    sx={{
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      color: grey[500],
                      lineHeight: '32px',
                      backgroundColor: 'transparent'
                    }}
                  >
                    {item.text}
                  </ListSubheader>
                )
              }
              if (item.isSubLabel === true) {
                return (
                  <ListSubheader
                    key={originalIndex}
                    disableSticky
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: grey[600],
                      lineHeight: '28px',
                      backgroundColor: 'transparent',
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  >
                    {item.text}
                  </ListSubheader>
                )
              }
              return (
                <MenuItemContent
                  key={originalIndex}
                  onClick={() => { handleMenuItemClick(item.action) }}
                  text={item.text}
                  menuStyles={{
                    ...menuStyles,
                    ...(item.isSubItem === true && { paddingLeft: theme.spacing(6) })
                  }}
                  disabled={item.disabled}
                  tooltipTitle={item.tooltipTitle}
                  chip={item.chip}
                />
              )
            })
          : <Box p={4}>
            {menuItems}
          </Box>}
      </Menu>
    </>
  )
}

export default Dropdown
